import { api } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";
import {
  IApiResponseLinhas,
  ILinha,
  IPrenativoLinha,
  ISalvarLinha,
} from "shared/interfaces/linha";

const montarParams = (params?: any) => ({
  sortBy: params?.sortBy,
  page: params?.page,
  limit: params?.pageSize,
  direction: params?.direction,
  filtrar: params?.search || "",
});

export const getLinhas = async (params?: any): Promise<ILinha[]> => {
  try {
    // Plano 23/09/2026: o BaseController devolve a lista em "data" (não em "items"); lendo "items"
    // o grid de Linhas ficava sempre vazio.
    const { data } = await api.get<IApiResponseLinhas>("/Linha", { params: montarParams(params) });
    return data?.data ?? [];
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao buscar as Linhas";
    getErrorsByApi(error, errorMessage);
    console.error("[API ERROR]", error?.response?.status, error);
    return [];
  }
};

export const getDadosPaginacaoLinhas = async (params?: any): Promise<number> => {
  try {
    const { data } = await api.get<IApiResponseLinhas>("/Linha", { params: montarParams(params) });
    return data?.totalRecords ?? 0;
  } catch (error: any) {
    getErrorsByApi(error, "Tivemos problemas internos ao buscar paginação");
    return 0;
  }
};

export const getLinhaPorId = async (idLinha: number): Promise<ILinha | null> => {
  if (!idLinha) throw new Error("Id da Linha não informado");
  try {
    const { data } = await api.get<{ data: ILinha }>(`/Linha/${idLinha}`);
    return data?.data ?? null;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] ||
      "Tivemos problemas internos ao buscar a Linha";
    getErrorsByApi(error, errorMessage);
    return null;
  }
};

/**
 * Plano 05/10/2026 (Frente 3): devolve o idLinha gravado (o POST /api/Linha responde
 * `{ data: { idLinha } }`) para o modal manter a linha recém-criada aberta e vincular prenativos.
 * `null` = falha (a mensagem da API já foi tratada por getErrorsByApi).
 */
export const postLinha = async (payload: ISalvarLinha): Promise<number | null> => {
  try {
    const { data } = await api.post<{ data?: { idLinha?: number } }>("/Linha", payload);
    return data?.data?.idLinha ?? 0;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] || "Não foi possível salvar a Linha";
    getErrorsByApi(error, errorMessage);
    return null;
  }
};

export const deleteLinha = async (idLinha: number): Promise<boolean> => {
  try {
    await api.delete(`/Linha?idLinha=${idLinha}`);
    return true;
  } catch (error: any) {
    const errorMessage =
      error?.response?.data?.errors?.Mensagens?.[0] || "Não foi possível excluir a Linha";
    getErrorsByApi(error, errorMessage);
    return false;
  }
};

/**
 * Prenativos disponíveis para vincular à linha (aba Prenativo do cadastro legado).
 */
export const getPrenativos = async (filtrar?: string): Promise<IPrenativoLinha[]> => {
  try {
    const { data } = await api.get<{ data: IPrenativoLinha[] }>("/Prenativo", {
      params: { page: 1, limit: 5000, filtrar: filtrar || "" },
    });
    return data?.data ?? [];
  } catch (error: any) {
    getErrorsByApi(error, "Tivemos problemas internos ao buscar os Prenativos");
    return [];
  }
};

export const vincularPrenativoLinha = async (
  idLinha: number,
  idPrenativo: number
): Promise<boolean> => {
  try {
    await api.post("/linha/prenativo", { idLinha, idPrenativo });
    return true;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível vincular o Prenativo à Linha");
    return false;
  }
};

export const desvincularPrenativoLinha = async (idLinhaPrenativo: number): Promise<boolean> => {
  try {
    await api.delete(`/linha/prenativo?idLinhaPrenativo=${idLinhaPrenativo}`);
    return true;
  } catch (error: any) {
    getErrorsByApi(error, "Não foi possível remover o Prenativo da Linha");
    return false;
  }
};
